import React, { useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Trophy, HelpCircle, Lightbulb, ImagePlus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import MentionInput from './MentionInput';

const PostCreator = ({ onPostCreated }) => {
  const [type, setType] = useState('update');
  const [content, setContent] = useState('');
  const [mentions, setMentions] = useState([]);
  const [imageUrl, setImageUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePost = async () => {
    if (!content.trim()) {
      toast.error('Please write something');
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post('/posts', {
        type,
        content,
        image_url: imageUrl || null,
        mentions,
      });
      toast.success('Posted successfully!');
      setContent('');
      setMentions([]);
      setImageUrl('');
      setType('update');
      onPostCreated(data);
    } catch (error) {
      toast.error('Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  const postTypes = [
    { value: 'win', label: 'Win', icon: Trophy, color: 'text-green-600' },
    { value: 'question', label: 'Question', icon: HelpCircle, color: 'text-blue-600' },
    { value: 'update', label: 'Update', icon: Lightbulb, color: 'text-slate-600' },
  ];

  return (
    <Card className="p-6 bg-white border border-slate-100 shadow-sm" data-testid="post-creator">
      <div className="flex gap-3">
        <Avatar className="h-10 w-10">
          <AvatarImage src={user?.profile_photo} />
          <AvatarFallback className="bg-[#1E3A8A] text-white text-sm">
            {getInitials(user?.name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <Tabs value={type} onValueChange={setType} className="mb-3">
            <TabsList className="grid w-full grid-cols-3">
              {postTypes.map((pt) => {
                const Icon = pt.icon;
                return (
                  <TabsTrigger
                    key={pt.value}
                    value={pt.value}
                    className="flex items-center gap-2"
                    data-testid={`post-type-${pt.value}`}
                  >
                    <Icon className={`h-4 w-4 ${pt.color}`} />
                    <span className="hidden sm:inline">{pt.label}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>

          <MentionInput
            value={content}
            onChange={setContent}
            mentions={mentions}
            onMentionsChange={setMentions}
            placeholder={
              type === 'win'
                ? 'Share your win with the community... (tip: type @ to mention an alumnus)'
                : type === 'question'
                ? 'Ask the community for help... (tip: type @ to mention an alumnus)'
                : "What's on your mind? Type @ to mention an alumnus"
            }
            className="mb-3"
            testId="post-content-input"
          />

          {imageUrl && (
            <div className="mb-3 relative">
              <img src={imageUrl} alt="Upload preview" className="rounded-md max-w-full h-auto block" />
              <Button
                size="sm"
                variant="destructive"
                className="absolute top-2 right-2"
                onClick={() => setImageUrl('')}
              >
                Remove
              </Button>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                id="image-upload"
              />
              <label htmlFor="image-upload">
                <Button variant="ghost" size="sm" className="gap-2" asChild>
                  <span>
                    <ImagePlus className="h-4 w-4" />
                    Add Image
                  </span>
                </Button>
              </label>
            </div>

            <Button
              onClick={handlePost}
              disabled={loading}
              className="bg-[#22C55E] hover:bg-[#16A34A] text-white font-bold shadow-md shadow-green-500/20"
              data-testid="post-submit-button"
            >
              {loading ? 'Posting...' : type === 'win' ? 'Share Win' : 'Post'}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default PostCreator;
